import React from "react";
import { Container, Grid, Skeleton, Box, Paper } from "@mui/material";
import { HeadingGradient } from "../../Gradient";

/**
 * Shimmer loading component for InkMastersDetailsPage
 * Matches the structure of InkMastersDetailsPage with hero banner, content sections, and prize cards
 */
const InkMastersDetailsPageShimmer = () => {
  return (
    <Box>
      {/* Hero Section Shimmer */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 340, md: 460 },
          backgroundImage:
            "url(https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0)",
          backgroundSize: "cover",
          backgroundPosition: "center",

          mt: -1,
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.75))"
                : "linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.65))",
          }}
        />

        {/* Frosted Title Panel Shimmer */}
        <Container
          maxWidth="md"
          sx={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              backgroundColor: "rgba(255,255,255,0.1)",
              px: { xs: 3, md: 5 },
              py: { xs: 2.5, md: 3 },
              borderRadius: 4,
              textAlign: "center",
            }}
          >
            {/* Ink Masters Label */}
            <Skeleton
              variant="text"
              width={200}
              height={16}
              sx={{ mx: "auto", mb: 1 }}
            />

            {/* Title */}
            <Skeleton
              variant="text"
              width="80%"
              height={48}
              sx={{ mx: "auto", mb: 1, mt: 1 }}
            />
            <Skeleton
              variant="text"
              width="60%"
              height={48}
              sx={{ mx: "auto" }}
            />

            {/* Dates */}
            <Skeleton
              variant="text"
              width={250}
              height={20}
              sx={{ mx: "auto", mb: 1, mt: 1 }}
            />

            {/* Hashtag Chip */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Skeleton
                variant="rounded"
                width={100}
                height={32}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Content Section Shimmer */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        {/* About Section Shimmer */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 5,
            borderRadius: 3,
            backgroundColor: "transparent",
            backdropFilter: "blur(12px)",
            border: (theme) =>
              `1px solid ${theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)"
              }`,
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 8px rgba(0,0,0,0.1)"
                : "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <HeadingGradient
              variant="h5"
              sx={{
                fontWeight: 700,
              }}
            >
              About the Challenge
            </HeadingGradient>
          </Box>

          {/* Description lines */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="95%" height={20} />
          </Box>

          {/* Theme Section */}
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width={100} height={24} sx={{ mb: 1 }} />
            <Skeleton
              variant="text"
              width="100%"
              height={18}
              sx={{ mb: 0.5 }}
            />
            <Skeleton variant="text" width="80%" height={18} />
          </Box>
        </Paper>

        {/* Rewards Section Shimmer */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <HeadingGradient
              variant="h5"
              sx={{
                fontWeight: 700,
              }}
            >
              🎁 Prizes
            </HeadingGradient>
          </Box>
          <Grid container spacing={3}>
            {[...Array(3)].map((_, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    textAlign: "center",
                    py: 3,
                    borderRadius: 3,
                    backgroundColor: "transparent",
                    backdropFilter: "blur(12px)",
                    border: (theme) =>
                      `1px solid ${theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                      }`,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 2px 8px rgba(0,0,0,0.1)"
                        : "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <Skeleton
                    variant="text"
                    width={120}
                    height={28}
                    sx={{ mx: "auto", mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width={100}
                    height={24}
                    sx={{ mx: "auto", mt: 1 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Winning Criteria Shimmer */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 2 }}>
            <HeadingGradient
              variant="h5"
              sx={{
                fontWeight: 700,
              }}
            >
              🧾 Winning Criteria
            </HeadingGradient>
          </Box>
          <Box sx={{ pl: 3, mt: 0.5 }}>
            {[...Array(3)].map((_, i) => (
              <Box key={i} sx={{ mb: 1 }}>
                <Skeleton variant="text" width="90%" height={18} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Participation Section Shimmer */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 2 }}>
            <HeadingGradient
              variant="h5"
              sx={{
                fontWeight: 700,
              }}
            >
              ✍️ How to Participate
            </HeadingGradient>
          </Box>
          <Box>
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="85%" height={20} />
          </Box>
        </Box>

        {/* CTA Button Shimmer */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <Skeleton
            variant="rounded"
            width={200}
            height={48}
            sx={{ borderRadius: 99 }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default InkMastersDetailsPageShimmer;
